/**
 * Tiny safe formula evaluator for game design formulas.
 * Supports numbers, variables, + - * / % ^, parentheses and a small set of
 * math functions. No eval / Function — a hand-rolled recursive descent parser.
 */

const FUNCTIONS = {
  min: (...args) => Math.min(...args),
  max: (...args) => Math.max(...args),
  floor: (x) => Math.floor(x),
  ceil: (x) => Math.ceil(x),
  round: (x) => Math.round(x),
  sqrt: (x) => Math.sqrt(x),
  abs: (x) => Math.abs(x),
  pow: (x, y) => x ** y,
  clamp: (x, lo, hi) => Math.min(Math.max(x, lo), hi),
};

const tokenize = (expression) => {
  const tokens = [];
  let i = 0;
  const src = String(expression);

  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) {
      i += 1;
    } else if (/[0-9.]/.test(ch)) {
      let num = '';
      while (i < src.length && /[0-9.]/.test(src[i])) {
        num += src[i];
        i += 1;
      }
      if ((num.match(/\./g) || []).length > 1) {
        throw new Error(`Invalid number "${num}"`);
      }
      tokens.push({ type: 'number', value: parseFloat(num) });
    } else if (/[a-zA-Z_]/.test(ch)) {
      let name = '';
      while (i < src.length && /[a-zA-Z0-9_]/.test(src[i])) {
        name += src[i];
        i += 1;
      }
      tokens.push({ type: 'identifier', value: name });
    } else if ('+-*/%^(),'.includes(ch)) {
      tokens.push({ type: ch });
      i += 1;
    } else {
      throw new Error(`Unexpected character "${ch}"`);
    }
  }
  return tokens;
};

/**
 * Evaluate a formula expression.
 * @param {string} expression - e.g. "floor(attack * power / defense) + 2"
 * @param {Object} variables - map of variable name -> number
 * @returns {{ value: number|null, error: string|null }}
 */
export const evaluateFormula = (expression, variables = {}) => {
  if (!expression || !String(expression).trim()) {
    return { value: null, error: null };
  }

  let tokens;
  try {
    tokens = tokenize(expression);
  } catch (err) {
    return { value: null, error: err.message };
  }

  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => {
    const token = tokens[pos];
    pos += 1;
    return token;
  };

  // Grammar: expr -> term (('+'|'-') term)*
  //          term -> factor (('*'|'/'|'%') factor)*
  //          factor -> unary ('^' factor)?
  //          unary -> '-' unary | primary
  //          primary -> number | name | name '(' args ')' | '(' expr ')'
  // The grammar is mutually recursive, so the parse functions reference each
  // other before their definitions.
  /* eslint-disable no-use-before-define */
  const parseExpr = () => {
    let left = parseTerm();
    while (peek() && (peek().type === '+' || peek().type === '-')) {
      const op = next().type;
      const right = parseTerm();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  };

  const parseTerm = () => {
    let left = parseFactor();
    while (peek() && (peek().type === '*' || peek().type === '/' || peek().type === '%')) {
      const op = next().type;
      const right = parseFactor();
      if (op === '*') left *= right;
      else if (op === '/') left /= right;
      else left %= right;
    }
    return left;
  };

  const parseFactor = () => {
    const base = parseUnary();
    if (peek() && peek().type === '^') {
      next();
      const exponent = parseFactor(); // right-associative
      return base ** exponent;
    }
    return base;
  };

  const parseUnary = () => {
    if (peek() && peek().type === '-') {
      next();
      return -parseUnary();
    }
    return parsePrimary();
  };

  const parsePrimary = () => {
    const token = next();
    if (!token) {
      throw new Error('Unexpected end of expression');
    }
    if (token.type === 'number') {
      return token.value;
    }
    if (token.type === '(') {
      const value = parseExpr();
      if (!peek() || next().type !== ')') {
        throw new Error('Missing closing parenthesis');
      }
      return value;
    }
    if (token.type === 'identifier') {
      const name = token.value;
      if (peek() && peek().type === '(') {
        next(); // consume '('
        const fn = FUNCTIONS[name.toLowerCase()];
        if (!fn) {
          throw new Error(`Unknown function "${name}"`);
        }
        const args = [];
        if (peek() && peek().type !== ')') {
          args.push(parseExpr());
          while (peek() && peek().type === ',') {
            next();
            args.push(parseExpr());
          }
        }
        if (!peek() || next().type !== ')') {
          throw new Error(`Missing closing parenthesis for ${name}()`);
        }
        return fn(...args);
      }
      if (Object.prototype.hasOwnProperty.call(variables, name)) {
        const value = Number(variables[name]);
        if (Number.isNaN(value)) {
          throw new Error(`Variable "${name}" is not a number`);
        }
        return value;
      }
      throw new Error(`Unknown variable "${name}"`);
    }
    throw new Error(`Unexpected token "${token.type}"`);
  };
  /* eslint-enable no-use-before-define */

  try {
    const value = parseExpr();
    if (pos !== tokens.length) {
      return { value: null, error: 'Unexpected trailing input' };
    }
    if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
      return { value: null, error: 'Expression did not evaluate to a finite number' };
    }
    return { value, error: null };
  } catch (err) {
    return { value: null, error: err.message };
  }
};

/** Extract variable names referenced in an expression (excluding functions). */
export const extractVariables = (expression) => {
  try {
    const tokens = tokenize(expression || '');
    const names = new Set();
    tokens.forEach((token, index) => {
      if (token.type === 'identifier') {
        const isFunctionCall = tokens[index + 1] && tokens[index + 1].type === '(';
        if (!isFunctionCall) {
          names.add(token.value);
        }
      }
    });
    return Array.from(names);
  } catch (err) {
    return [];
  }
};

export default evaluateFormula;
